import {
  useMemo,
  useRef,
  useContext,
  useEffect,
  useState,
  Fragment,
} from 'react'
import { useRouter } from 'next/router'
import _omit from 'lodash/omit'
import _pick from 'lodash/pick'
import _isEmpty from 'lodash/isEmpty'
import _isEqual from 'lodash/isEqual'
import { PAGINATION_LIMIT } from 'constants/api'
import ListCard from 'app/ProviderSearch/ListCard'
import ListCardSkeleton from 'common/Skeletons/ListCardSkeleton'
import classNames from 'classnames'
import ErrorComponent from 'common/ErrorComponent'
import EmptySearchResult from 'app/ProviderSearch/EmptySearchResult'
import { ProviderFilterTypes } from 'dataSource/ProviderSearch'
import {
  formatPracticesFromProviders,
  isOnlyAllProviderFilter,
  getGenderValuesFromIds,
  getSlotsStartEndDate,
  formatSupplyReserveParams,
  hasSecondaryFilters,
  getSlotWindowStart,
} from 'app/ProviderSearch/utils'
import qs from 'qs'
import {
  fetchDataAsync,
  hasError,
  getParsedQuery,
  getCurrentZonnedTime,
  isDateEqual,
  modifyURLQuery,
} from 'utils/common'
import useApi from 'hooks/useApi'
import { PROVIDERS, PRACTICES } from 'dataSource/ProviderSearch/routes'
import SlotDateContext from 'context/ProviderBooking/SlotDate'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import { InsuranceProviderContext } from 'context/common/InsuranceProvider'
import ProviderListPagination from '../ProviderListPaginationV3'
import ProviderSlotContext from 'context/ProviderBooking/ProviderSlots'
import { sessionStorageKeys, DayOfWeek } from 'constants/common'
import toDate from 'date-fns/toDate'
import addDays from 'date-fns/addDays'
import JumpToDate from '@app/care/v1/components/AppointmentsDatePicker/JumpToDate'
import { callRudderEventForAvailableProviderShown } from 'app/ProviderSearch/rudderEventUtils'
import useThriveAuth from 'hooks/useThriveAuth'
import DatePaginator from 'common/DatePaginator'
import useBreakpoint from 'hooks/useBreakPoint'
import { Button } from 'component_library/1_Atoms'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { getTreatment } from 'utils/treatment'
import PageLoader from 'common/PageLoader'
import {
  SLOT_LOCATION_MAP,
  SECONDARY_FILTERS,
  SLOT_CALENDAR_LAST_DAY_OFFSET_FROM_ANCHOR,
  EXTENDED_BOOKING_LAST_DAY_OFFSET_FROM_ANCHOR,
} from 'app/ProviderSearch/constants'

import styles from './index.module.scss'
import commonStyles from '../common.module.scss'
import DatePaginatorSkeleton from 'common/Skeletons/DatePaginatorSkeleton'
import { LinkElement } from 'app/ProviderSearch/LeadGenerationElements'
import { useLeadGenContext } from 'context/LeadGen'
import usePreviousValue from 'hooks/usePreviousValue'
import { CachePolicies } from 'use-http'
import { If } from '@/utility_components/ControlStatements'
import SearchValueProp from 'components/app/ProviderSearch/SearchValueProp'
import SearchValuePropSource from 'components/app/ProviderSearch/SearchValueProp/Source'
import useIsValuePropShown from 'components/app/ProviderSearch/SearchValueProp/useIsValuePropShown'
import { getFilterValueFromId } from 'app/ProviderDetails/utils'
import { isServer } from 'utils/environment'
import Divider from 'common/Divider'
import { format } from 'date-fns'

const omitQueryArray = ['partnershipCode']

const ProvidersList: React.FC<ProvidersListProps> = ({
  groupedFilters,
  providerList,
  providerListLoading,
  providerPagination,
  onSetPracticesList,
  selectedPractice,
  onSelectPractice,
  shouldFetchPractices = true,
  scrollContainer,
  inPersonFilterSelected,
  providerCardDetails,
  countDetails,
  isWaterfallFlow = false,
  isZeroProviderState = false,
  providerListError = null,
  providerListResult = null,
  intakeSupplyReserveData = {},
  waterfallProviderInsuranceInfo,
  showEmptyScreen = false,
  extendedBookingEnabled = false,
  extendedBookingLoading = false,
  showWaterfallDivider = true,
  dowFallbackActive = false,
}) => {
  const router = useRouter()
  const { query } = router
  const userSelectedFilters = getFilterValueFromId(groupedFilters, query)
  const { state, location } = query
  const dayOfWeekInQuery = query['dayOfWeek[]']
  const dayOfWeekFromQuery = useMemo<DayOfWeek[] | undefined>(() => {
    // When the waterfall has dropped DOW as a fallback the date paginator must
    // show a normal consecutive range, not DOW-filtered columns — even though
    // the chip and URL still carry the original selection.
    if (dowFallbackActive || !dayOfWeekInQuery) {
      return undefined
    }

    const values = Array.isArray(dayOfWeekInQuery)
      ? dayOfWeekInQuery
      : [dayOfWeekInQuery]
    const known = new Set<string>(Object.values(DayOfWeek))
    const parsed = values.filter(
      (v): v is DayOfWeek => typeof v === 'string' && known.has(v)
    )

    return parsed.length > 0 ? parsed : undefined
  }, [
    dowFallbackActive,
    Array.isArray(dayOfWeekInQuery)
      ? dayOfWeekInQuery.join(',')
      : dayOfWeekInQuery,
  ])
  const showValueProp = useIsValuePropShown()
  // Computed here because the map callback below shadows `providerCardDetails`
  // with the per-item value; SearchValueProp uses it to skip the last provider.
  const totalProviders = providerCardDetails?.length ?? 0
  const { isSupportOrThirdPartyUser, user } = useThriveAuth()
  const { businessPartner } = user ?? {}
  const { request: apiRequest } = useApi({
    redirectOnUnauthorized: false,
    cachePolicy: CachePolicies.CACHE_FIRST,
  })
  const previousQueryRef = useRef<string>('')
  const { timeZone } = useContext(TimeZoneContext)
  const focusProviderCardRef = useRef<HTMLDivElement>(null)
  const { startSlotDate, setStartSlotDate, slotListLimit } = useContext(
    SlotDateContext
  )
  const [selectedDate, setSelectedDate] = useState<Date>(null)
  const { insuranceProvider } = useContext(InsuranceProviderContext)
  const { providerSlots, loading: slotsLoading } = useContext(
    ProviderSlotContext
  )
  const prevSlotsLoading = usePreviousValue(slotsLoading)

  const previousSelectedSlot: SelectedSlot = useMemo(() => {
    if (isServer()) {
      return null
    }

    const raw = sessionStorage.getItem(
      sessionStorageKeys.PREVIOUS_SELECTED_SLOT
    )

    return raw ? JSON.parse(raw) : null
  }, [providerList])

  const { isExtraLargeSize, isAboveXLSize } = useBreakpoint()
  const isDesktop = isExtraLargeSize || isAboveXLSize

  /**
   * - This function makes an API call to an explicit practice API to fetch the practices rather then
   *   calling each paginated providers API and extracting the practices from it.
   *  - This practices API's response is cached from the backend and only caches the per location responses without any filtered applied.
   *  - Thats why this function is only called when there is no filtered applied.
   */
  const fetchPractices = async (queryObj: qs.ParsedQs) => {
    const formattedQuery = qs.stringify(
      _omit(queryObj, [
        'limit',
        'location',
        'slotStartDate',
        'slotEndDate',
        'partnershipCode',
      ]),
      {
        skipNulls: true,
      }
    )
    const requestUrl = `${PRACTICES}?${formattedQuery}`
    const responsePracticesList = await apiRequest.get(requestUrl)
    hasError(apiRequest, responsePracticesList)
    onSetPracticesList?.(responsePracticesList)

    // If practiceId is present and no selected practice, find and set it
    const { practiceId } = queryObj

    if (practiceId && _isEmpty(selectedPractice)) {
      const practice = responsePracticesList?.find(
        (practice) => practice.id === practiceId
      )

      if (practice) {
        onSelectPractice(practice)
      }
    }
  }

  /**
   * - This function makes the rest of the paginated provider API calls to get all the providers
   *    then extract the practices from all these providers
   *    this api calls are being made in the background, meaning no loaders are shown for these providers
   *  - This function is called when ever a filter is applied.
   *  - When no filters is applied then fetchPractices function is called
   */
  const fetchRestProvidersForPractice = async (
    { providerListResult, pagination },
    parsedQuery
  ) => {
    const { practiceId } = parsedQuery

    const parsedLatestQuery = _omit(parsedQuery, omitQueryArray)
    // Although this is omitted in the ref we are just double making sure
    const parsedPreviousQuery = _omit(
      qs.parse(previousQueryRef.current),
      omitQueryArray
    )

    if (!_isEqual(parsedLatestQuery, parsedPreviousQuery)) {
      let practices = formatPracticesFromProviders(providerListResult)
      onSetPracticesList?.(practices)

      const providerDataPromises = fetchDataAsync(
        parsedQuery,
        PROVIDERS,
        apiRequest,
        pagination
      )

      for (const providerDataPromise of providerDataPromises) {
        const providerData = await providerDataPromise
        const { result: providers = [] } = providerData ?? {}
        practices = formatPracticesFromProviders(providers, practices)
        onSetPracticesList?.([...practices])
      }

      // If practiceId is present and there is no selected practice state then this would
      // find the practice from the list of practices and set it
      // this case would come only when a user has a selected practice and refreshes the page
      if (practiceId && _isEmpty(selectedPractice)) {
        const practice = practices.find(
          (practice) => practice.id === practiceId
        )
        onSelectPractice(practice)
      }
    }
  }

  const fetchAllPracticesForMap = async (parsedQuery): Promise<void> => {
    try {
      if (!shouldFetchPractices) {
        return
      }

      const { slotStartDate, partnershipCode } = parsedQuery
      const utcStartTime = zonedTimeToUtc(
        toDate(parseInt(slotStartDate)),
        Intl.DateTimeFormat().resolvedOptions().timeZone
      )

      const { startDate, endDate } = getSlotsStartEndDate(
        utcToZonedTime(utcStartTime, timeZone),
        slotListLimit,
        timeZone,
        dayOfWeekFromQuery,
        undefined,
        extendedBookingEnabled
      )
      const isSupplyReserveFlagEnabled =
        getTreatment(TreatmentFlags.SUPPLY_RESERVE, {
          state,
        }) === treatmentFlagValues.ON
      const {
        id: b2bPartnerId,
        isActive: isB2bPartnerActive,
        partnershipCode: existingB2bPartnerKeyword,
      } = businessPartner ?? {}
      const isExistingB2bUser = isB2bPartnerActive && !!b2bPartnerId
      const b2bPartnershipCode = isExistingB2bUser
        ? existingB2bPartnerKeyword
        : partnershipCode

      const queryObj = {
        ..._pick(parsedQuery, [
          'state',
          'longitude',
          'latitude',
          'offset',
          'limit',
          'areaOfConcern',
          'insuranceId',
          'counselingType',
          'availabilityType',
          'orderBy',
          'gender',
          'clientAgeGroup',
          'language',
          'counselingApproach',
          'practiceId',
        ]),
        slotStartDate: startDate.toISOString(),
        slotEndDate: endDate.toISOString(),
        ...(isSupplyReserveFlagEnabled &&
          !!b2bPartnershipCode && { partnershipCode: b2bPartnershipCode }),
      }
      const isAllProviderFilter = isOnlyAllProviderFilter(queryObj)

      if (isAllProviderFilter) {
        //To render Maps first in case practices are fetched separately as it is the largest Content
        fetchPractices(queryObj)
      } else {
        //When filters other than All providers are applied, practices are extracted from Provider Details
        if (_isEmpty(providerListResult)) {
          // When practiceId is present, still fetch practices to populate selectedPractice for the map
          if (parsedQuery.practiceId) {
            fetchPractices(queryObj)
          } else {
            onSetPracticesList?.([])
          }

          return
        }

        fetchRestProvidersForPractice(
          { providerListResult, pagination: providerPagination },
          queryObj
        )
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch practices for map:', error)
    }
  }

  const onRemovePractice = () => {
    modifyURLQuery(router, {
      practiceId: undefined,
    })
  }

  const isExtendedBookingVisible =
    extendedBookingEnabled && !extendedBookingLoading

  const currentDate = getCurrentZonnedTime(timeZone)
  const maxSlotDate = addDays(
    currentDate,
    // Use the extended cap while the treatment is resolving so navigation is
    // not clamped to 3 weeks prematurely.
    extendedBookingEnabled || extendedBookingLoading
      ? EXTENDED_BOOKING_LAST_DAY_OFFSET_FROM_ANCHOR
      : SLOT_CALENDAR_LAST_DAY_OFFSET_FROM_ANCHOR
  )

  const handleSlotDateChange = (date: Date) => {
    setSelectedDate(date)

    const slotWindowStart = isExtendedBookingVisible
      ? getSlotWindowStart(date, maxSlotDate, slotListLimit, currentDate)
      : date

    modifyURLQuery(router, {
      slotStartDate: slotWindowStart.getTime().toString(),
      offset: '0',
    })
  }

  const jumpToDateDisabled = {
    before: currentDate,
    after: maxSlotDate,
  }

  const getPracticeLocationText = () => {
    let practiceLocationText = 'near '

    if (!!selectedPractice) {
      practiceLocationText = `for the ${selectedPractice.name} only.`
    }

    return practiceLocationText
  }

  const getProviderSearchQuery = () => {
    const qsQuery = getParsedQuery(router)
    const {
      offset = '0',
      limit = PAGINATION_LIMIT.toString(),
      slotStartDate = new Date().getTime().toString(),
      latitude,
      longitude,
      state,
      gender,
      ...restQuery
    } = qsQuery

    const genderValues = getGenderValuesFromIds(
      gender as string[],
      groupedFilters[ProviderFilterTypes.GENDER]
    )

    return {
      offset,
      limit,
      slotStartDate,
      latitude,
      longitude,
      state,
      ...(genderValues && { gender: genderValues }),
      ...restQuery,
    }
  }

  const { forwardingFilters } = useLeadGenContext()

  const getWaterfallLocation = () => {
    if (selectedPractice) {
      return `in ${selectedPractice.city ? `${selectedPractice.city},` : ''}${
        selectedPractice.state || ''
      }, ${selectedPractice.country}`
    }

    return `in ${location}` || ''
  }

  useEffect(() => {
    // This useEffect is created to sync the context date state with the date that is in url query

    if (startSlotDate) {
      const { slotStartDate } = getParsedQuery(router)

      if (slotStartDate) {
        const queryStartDate = toDate(parseInt(slotStartDate as string))

        if (!isDateEqual(queryStartDate, startSlotDate)) {
          setStartSlotDate(queryStartDate)
        }
      }
    }
  }, [router.asPath, startSlotDate])

  useEffect(() => {
    if (shouldFetchPractices && !_isEmpty(providerListResult)) {
      const providerSearchQuery = getProviderSearchQuery()
      fetchAllPracticesForMap(providerSearchQuery)
    }
  }, [providerListResult, shouldFetchPractices])

  /*=====================This useEffect contains scroll logic===============================*/
  useEffect(() => {
    if (previousSelectedSlot?.slot && focusProviderCardRef?.current) {
      scrollContainer?.scrollTo({
        behavior: 'smooth',
        top: focusProviderCardRef.current.offsetTop,
      })
      sessionStorage.setItem(sessionStorageKeys.PREVIOUS_SELECTED_SLOT, null)
    }
  }, [providerSlots, previousSelectedSlot])

  useEffect(() => {
    // This useEffect has been added to satisfy the following requirements for triggering this event
    // 1. The slots must be loaded along with all the providers list
    // 2. If the API for providers fails the providerListResult still remains null which will not trigger this event,
    //providerSlots in this case remains [] which would falsely trigger this event
    //Pass intake supply params to the available providers shown event only when supply reserve flag is on
    const isSupplyReserveFlagEnabled =
      getTreatment(TreatmentFlags.SUPPLY_RESERVE, {
        state,
      }) === treatmentFlagValues.ON
    const shouldSupplyReserve =
      isSupplyReserveFlagEnabled &&
      (intakeSupplyReserveData.shouldReserveIntakeSupply ||
        intakeSupplyReserveData.shouldReservePsychiatryIntakeSupply)
    // Trigger this useEffect only when slotsLoading transitions from true to false (using prevSlotsLoading value)

    if (
      providerListResult &&
      !slotsLoading &&
      (prevSlotsLoading || shouldSupplyReserve)
    ) {
      const supplyReserveParams = isSupplyReserveFlagEnabled
        ? formatSupplyReserveParams(
            intakeSupplyReserveData,
            user?.userType,
            userSelectedFilters.counseling_type
          )
        : {}

      // In desktop view, the user is shown 5 days worth of slots for each provider.
      // But in mobile view, the user is only shown the next available slot for each provider.
      // So, in mobile view, set slot_sum to next_available_slot_sum so that Rudderstack tracking is accurate.
      if (!isDesktop) {
        countDetails.slot_sum = countDetails.next_available_slot_sum
      }

      if (!providerListLoading && !isWaterfallFlow) {
        callRudderEventForAvailableProviderShown({
          providers: providerListResult,
          countDetails,
          totalProvidersAvailable: providerPagination?.count,
          groupedFilters,
          query,
          startSlotDate,
          selectedPractice,
          user,
          intakeSupplyReserveParams: supplyReserveParams,
          timeZone,
        })
      }

      if (
        !providerListLoading &&
        isWaterfallFlow &&
        !hasSecondaryFilters(getParsedQuery(router), SECONDARY_FILTERS)
      ) {
        callRudderEventForAvailableProviderShown({
          providers: providerListResult,
          countDetails,
          totalProvidersAvailable: providerPagination?.count,
          groupedFilters,
          query,
          startSlotDate,
          selectedPractice,
          user,
          intakeSupplyReserveParams: supplyReserveParams,

          directProvidersCount: providerListResult?.length,
          indirectProvidersCount: 0,
          directSlotsCountDetails: countDetails,
          indirectSlotsCountDetails: { slot_sum: 0, provider_count: [] },
          timeZone,
        })
      }
    }
  }, [providerListResult, slotsLoading, prevSlotsLoading, isWaterfallFlow])

  const renderInNetworkSection = () => {
    return (
      <div className="~flex ~items-center">
        <img
          src="/icons/shield-check2.svg"
          srcSet="/icons/shield-check2.webp"
          alt="insurance in-network"
          className="~m-[6px] ~h-5 ~w-5"
        />
        <p className="~font-medium ~font-gibson ~text-sm ~p-[5px]">
          Accepts {insuranceProvider?.name}
        </p>
      </div>
    )
  }

  const isWaterfallFlowWithNoProviders =
    isWaterfallFlow && !providerList?.length && !isZeroProviderState

  const {
    noWaterfallProviderAcceptsInsurance,
    notInNWProvidersCount,
  } = waterfallProviderInsuranceInfo

  useEffect(() => {
    if (providerListLoading) {
      // Scrolls to the top before user provider api call
      scrollContainer?.scrollTo({
        top: 0,
      })
    }
  }, [providerListLoading])

  return (
    <>
      <ErrorComponent error={providerListError} />
      <PageLoader
        withSkeleton
        isLoading={providerListLoading}
        isEmpty={showEmptyScreen}
        loadingMessage="Finding Amazing Providers"
        loadingContainerClass={styles.provider_list_loader_container}
        Components={{
          Loading: (
            <>
              {isDesktop && <DatePaginatorSkeleton />}
              <ListCardSkeleton />
              <ListCardSkeleton />
              <ListCardSkeleton />
              <ListCardSkeleton />
              <ListCardSkeleton />
            </>
          ),
          Empty: <EmptySearchResult />,
        }}
      >
        <div className="~flex ~items-center ~justify-center">
          <div
            className={classNames(
              '~flex ~items-center ~justify-end lg:~justify-center ~flex-col ~gap-6 lg:~gap-10',
              styles.no_insurance_match,
              {
                [styles.no_insurance_match_visible]:
                  isWaterfallFlowWithNoProviders &&
                  noWaterfallProviderAcceptsInsurance,
              }
            )}
          >
            <img
              src="/icons/waterfall-no-in-nw-providers.svg"
              alt="no in-network providers"
              className="~h-[177px] ~w-[295px]"
            />
            <p className="~text-base lg:~text-lg ~font-medium ~text-center ~font-gibson">
              Currently, there are no in-network providers available, but{' '}
              {notInNWProvidersCount} providers below accept self-pay.
            </p>
            <p className="~text-base ~text-center ~font-gibson ~mb-[59px]">
              Want to know when more become available?{' '}
              {!isAboveXLSize && <br />}
              <LinkElement forwardingFilters={forwardingFilters}>
                Notify me
              </LinkElement>
            </p>
          </div>
        </div>

        <div className={styles.new_date_paginator_container}>
          {!isWaterfallFlow && (
            <div className={styles.providers_found_container}>
              {providerPagination.count >= 1 && (
                <span className={styles.providers_found_text}>
                  {`${providerPagination.count} provider${
                    providerPagination.count > 1 ? 's' : ''
                  } found`}
                  &nbsp;
                </span>
              )}
              <span
                className={classNames(
                  {
                    'mr-2': !!selectedPractice,
                  },
                  styles.practice_location_btn
                )}
              >
                {getPracticeLocationText()}
                {!selectedPractice && location}
              </span>
              {selectedPractice && (
                <Button
                  variant="secondary"
                  className={styles.clear_practice_btn}
                  onClick={onRemovePractice}
                >
                  Clear practice
                </Button>
              )}
            </div>
          )}

          {isDesktop && (
            <div className="~flex ~items-center ~gap-5 ~ml-auto">
              <If condition={isExtendedBookingVisible}>
                <div className="~flex ~relative">
                  <JumpToDate
                    selectedDate={selectedDate || startSlotDate}
                    onDateChange={handleSlotDateChange}
                    displaySelectedDate={false}
                    disabled={jumpToDateDisabled}
                    autoApply={true}
                    triggerClassName="~h-[44px] ~rounded-sm data-[state=open]:~bg-sunrise-30 ~border-none ~group ~peer"
                    footerContent={(handleSelectDate) => (
                      <div className="~border-t ~border-t-grey-wash ~px-4 ~py-5">
                        <p className="~text-sm ~font-medium ~text-center ~font-gibson">
                          Availability posted through{' '}
                          <span
                            role="button"
                            tabIndex={0}
                            className="~text-link hover:~cursor-pointer hover:~underline"
                            onClick={() => handleSelectDate(maxSlotDate)}
                          >
                            {format(maxSlotDate, 'MM/dd/yyyy')}
                          </span>
                        </p>
                      </div>
                    )}
                  />
                  <Divider className="!~h-full ~absolute ~right-px ~w-px ~bg-grey peer-hover:~hidden peer-data-[state=open]:~hidden" />
                </div>
              </If>
              <DatePaginator
                selectedDate={startSlotDate}
                highlightedDate={
                  isExtendedBookingVisible
                    ? selectedDate || startSlotDate
                    : undefined
                }
                extendedBookingEnabled={extendedBookingEnabled}
                extendedBookingLoading={extendedBookingLoading}
                onDateChange={handleSlotDateChange}
                weekDayContainerClass={styles.date_paginator_week_day_container}
                lowerDateFormat="MONTH_DATE_FORMAT"
                dayOfWeek={dayOfWeekFromQuery}
              />
            </div>
          )}
        </div>
        {isWaterfallFlowWithNoProviders &&
          !noWaterfallProviderAcceptsInsurance && ( // Empty state for no insurance is handled above the Date paginator
            <section
              className={classNames(styles.empty_container, {
                [styles.empty_container__mobile]: !isDesktop,
              })}
            >
              <header
                className={classNames(styles.empty_container_header, '~mb-6')}
              >
                We can&apos;t find providers matching all your filters. Want to
                know when more become available?{' '}
                <LinkElement forwardingFilters={forwardingFilters}>
                  Notify me
                </LinkElement>
              </header>
              <p className={styles.empty_container_body}>
                We found providers that match some or most of your filters
                below.
              </p>
            </section>
          )}
        {/* This div is intentional to handle gradient */}
        <div>
          {providerList?.length > 0 && isWaterfallFlow && (
            <section
              className={classNames(styles.waterfall_text_container, {
                [styles.waterfall_text_container__mobile]: !isDesktop,
                [styles.waterfall_text_container__gradient]:
                  isDesktop && isWaterfallFlow,
              })}
            >
              <span className={styles.waterfall_text_bold}>
                {`${providerPagination.count} provider${
                  providerPagination.count > 1 ? 's' : ''
                }`}
                &nbsp;
              </span>
              <span>{`${getWaterfallLocation()} ${
                providerPagination.count > 1 ? 'are' : 'is'
              } a `}</span>
              <span className={styles.waterfall_text_bold}>100% match</span>
              {selectedPractice && (
                <Button
                  variant="secondary"
                  className={styles.clear_practice_btn}
                  onClick={onRemovePractice}
                >
                  Clear practice
                </Button>
              )}
            </section>
          )}

          {/* Scrollable section */}
          {providerCardDetails?.map((providerCardDetails, index) => {
            const { providerDetails } = providerCardDetails
            const isFocusCard =
              providerDetails.id === previousSelectedSlot?.provider?.id

            const refToUse =
              isFocusCard && focusProviderCardRef ? focusProviderCardRef : null

            return (
              <Fragment key={`${providerDetails.id}-list-card`}>
                <div ref={refToUse} className={styles.ProviderCard_container}>
                  <ListCard
                    groupedFilters={groupedFilters}
                    showDistance={
                      inPersonFilterSelected || isSupportOrThirdPartyUser
                    }
                    providerCardDetails={providerCardDetails}
                    slotLocation={SLOT_LOCATION_MAP.direct_match}
                    renderCustomInsuranceSection={
                      //Show the in-network insurance section if an insurance is selected (both waterfall and non-waterfall flows)
                      insuranceProvider?.id && renderInNetworkSection
                    }
                  />
                </div>
                <SearchValueProp
                  index={index}
                  showValueProp={showValueProp}
                  total={totalProviders}
                />
              </Fragment>
            )
          })}
          {isWaterfallFlow &&
            showWaterfallDivider &&
            hasSecondaryFilters(getParsedQuery(router), SECONDARY_FILTERS) && (
              <div
                className={classNames(
                  commonStyles.waterfall_bucket_divider_container,
                  {
                    [commonStyles.waterfall_bucket_divider_container__mobile]: !isDesktop,
                  }
                )}
              >
                <img
                  src={
                    isDesktop
                      ? '/images/waterfall-bucket-divider.svg'
                      : '/images/squiggle.svg'
                  }
                  alt="Waterfall bucket divider"
                  className={commonStyles.waterfall_bucket_divider}
                />
              </div>
            )}
        </div>
        <ProviderListPagination providerPagination={providerPagination} />
        {/* Hidden in waterfall (there it attaches under the last waterfall
            control, in ProvidersListWaterfall) and when no value prop card
            renders — a card needs 2+ providers, so the source would be orphaned. */}
        <If condition={!isWaterfallFlow && showValueProp && totalProviders > 1}>
          <SearchValuePropSource />
        </If>
      </PageLoader>
    </>
  )
}

export default ProvidersList
